<template>
  <div class="mv2-page">
    <!-- ========== Hero ========== -->
    <header class="mv2-hero">
      <h1>Places We've Been</h1>
      <p>温柔地足迹我们走过的每一座城市。</p>
    </header>

    <!-- ========== Maps Grid ========== -->
    <section class="mv2-maps">
      <div class="mv2-map-card">
        <div class="mv2-map-card__head">
          <h2>中国地图</h2>
          <span v-if="travelStats" class="mv2-map-card__badge">{{ travelStats.totalCities || 0 }} 座城市</span>
        </div>
        <div ref="chinaMapRef" class="mv2-map-card__canvas"></div>
      </div>
      <div class="mv2-map-card">
        <div class="mv2-map-card__head">
          <h2>世界地图</h2>
          <span v-if="travelStats" class="mv2-map-card__badge">{{ travelStats.totalProvinces || 0 }} 个地区</span>
        </div>
        <div ref="worldMapRef" class="mv2-map-card__canvas"></div>
      </div>
    </section>

    <!-- ========== Travel List (collapsible) ========== -->
    <section class="mv2-travels-section">
      <div class="mv2-travels-head">
        <div class="mv2-travels-head__left">
          <span class="material-symbols-outlined">flight_takeoff</span>
          <span>足迹列表</span>
          <span v-if="travels.length" class="mv2-travels-count">{{ travels.length }}</span>
        </div>
        <div class="mv2-travels-head__right">
          <button v-if="!isVisitor" class="mv2-btn-outline" @click="openTravelCreate">
            <span class="material-symbols-outlined" style="font-size:16px">add</span> 记录旅行
          </button>
          <button v-if="travels.length" class="mv2-btn-icon" @click="travelListExpanded = !travelListExpanded">
            <span class="material-symbols-outlined" :style="{ transform: travelListExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }">expand_more</span>
          </button>
        </div>
      </div>

      <div v-loading="loadingTravels">
        <div v-if="!loadingTravels && !travels.length" class="mv2-empty">
          <span class="material-symbols-outlined" style="font-size:40px;color:var(--mv-outline)">pin_drop</span>
          <p>{{ isVisitor ? '暂无旅行记录' : '还没有旅行记录' }}</p>
          <button v-if="!isVisitor" class="mv2-btn-outline" @click="openTravelCreate">记录第一次旅行</button>
        </div>
        <Transition name="mv-collapse">
          <div v-if="travelListExpanded && travels.length" class="mv2-travel-list">
            <div v-for="item in travels" :key="item.id" class="mv2-travel-item">
              <div class="mv2-travel-item__dot"></div>
              <div class="mv2-travel-item__card">
                <div class="mv2-travel-item__top">
                  <strong>{{ item.city }}</strong>
                  <span v-if="item.province" class="mv2-travel-item__sub">{{ item.province }}, {{ item.country || '中国' }}</span>
                  <span v-else-if="item.country && item.country !== '中国'" class="mv2-travel-item__sub">{{ item.country }}</span>
                </div>
                <p v-if="item.description" class="mv2-travel-item__desc">{{ item.description }}</p>
                <div class="mv2-travel-item__date">{{ item.startDate || '\u2014' }}<span v-if="item.endDate"> ~ {{ item.endDate }}</span></div>
                <div v-if="!isVisitor" class="mv2-travel-item__ops">
                  <button @click="openTravelEdit(item)">编辑</button>
                  <button class="danger" @click="removeTravel(item)">删除</button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </section>

    <!-- ========== 旅行新增/编辑弹窗 ========== -->
    <el-dialog v-model="travelDialogVisible" width="min(92vw,480px)" class="pv4-dialog" modal-class="pw-blur-mask" destroy-on-close append-to-body>
      <div class="pv4-dialog-content">
        <div class="sv-dialog-top">
          <h3 class="pv4-dialog-title">{{ travelEditing ? '编辑旅行' : '记录旅行' }}</h3>
          <button class="sv-dialog-close" @click="travelDialogVisible = false">&times;</button>
        </div>
        <div class="pv4-dialog-form">
          <div class="pv4-form-grid">
            <div class="pv4-field pv4-field--full">
              <label>国家</label>
              <el-select v-model="travelForm.country" filterable placeholder="选择国家" style="width:100%" @change="onCountryChange">
                <el-option v-for="c in countryOptions" :key="c" :label="c" :value="c" />
              </el-select>
            </div>
            <div v-if="travelForm.country === '中国'" class="pv4-field pv4-field--full">
              <label>省 / 市</label>
              <el-cascader
                v-model="regionCodes"
                :options="pcTextArr"
                :props="regionCascaderProps"
                filterable
                clearable
                placeholder="选择省份 / 城市"
                style="width:100%"
                @change="onRegionChange"
              />
            </div>
            <div v-if="travelForm.country !== '中国'" class="pv4-field pv4-field--full">
              <label>城市</label>
              <el-select v-model="travelForm.city" filterable allow-create default-first-option placeholder="选择或输入城市" style="width:100%">
                <el-option v-for="c in worldCityOptions" :key="c" :label="c" :value="c" />
              </el-select>
            </div>
            <div v-if="travelForm.country !== '中国'" class="pv4-field">
              <label>经度</label>
              <el-input v-model="travelForm.longitude" placeholder="如 125.560314" />
            </div>
            <div v-if="travelForm.country !== '中国'" class="pv4-field">
              <label>纬度</label>
              <el-input v-model="travelForm.latitude" placeholder="如 -8.556856" />
            </div>
            <div class="pv4-field pv4-field--full">
              <label>日期</label>
              <el-date-picker
                v-model="travelForm.dateRange"
                type="daterange"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                start-placeholder="出发"
                end-placeholder="结束"
                style="width:100%"
              />
            </div>
            <div class="pv4-field pv4-field--full">
              <label>旅行描述</label>
              <el-input v-model="travelForm.description" type="textarea" :rows="3" placeholder="记录这次旅行…" resize="none" />
            </div>
          </div>
        </div>
        <div class="pv4-dialog-footer">
          <el-button round @click="travelDialogVisible = false">取消</el-button>
          <el-button type="danger" round :loading="submittingTravel" @click="submitTravel">
            {{ travelEditing ? '保存' : '添加' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 全屏图片预览 -->
    <Teleport to="body">
      <Transition name="mv-fs">
        <div v-if="previewVisible" class="mv-preview-mask" @click="previewVisible = false">
          <button class="mv-preview-close" @click="previewVisible = false">&times;</button>
          <button v-if="previewList.length > 1" class="mv-preview-arrow mv-preview-arrow--left" @click.stop="previewIdx = (previewIdx - 1 + previewList.length) % previewList.length">&#8249;</button>
          <img :src="previewList[previewIdx]" alt="" class="mv-preview-img" @click.stop>
          <button v-if="previewList.length > 1" class="mv-preview-arrow mv-preview-arrow--right" @click.stop="previewIdx = (previewIdx + 1) % previewList.length">&#8250;</button>
          <div v-if="previewList.length > 1" class="mv-preview-counter">{{ previewIdx + 1 }} / {{ previewList.length }}</div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as echarts from 'echarts'
import {
  createTravel, updateTravel, deleteTravel as delTravel, getTravelList, getTravelStats, getTravelMap
} from '../api/travel'
import { pcTextArr } from 'element-china-area-data'
import countries from 'i18n-iso-countries'
import zhLocale from 'i18n-iso-countries/langs/zh.json'

countries.registerLocale(zhLocale)

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('us_space_user') || 'null') } catch { return null }
}
const isVisitor = computed(() => getStoredUser()?.role === 'visitor')

const chinaCityCoords = {}
const worldCityCoords = {}
const stripSuffix = (s) => s ? s.replace(/(省|市|特别行政区|维吾尔自治区|壮族自治区|回族自治区|藏族自治区|自治区)$/, '') : ''

const normalizeProvinceName = (name) => {
  const value = stripSuffix(name || '')
  if (!value) return ''
  return value
    .replace('新疆维吾尔', '新疆')
    .replace('内蒙古', '内蒙')
    .replace('广西壮族', '广西')
    .replace('宁夏回族', '宁夏')
    .replace('西藏藏族', '西藏')
}

const resolveChinaCoord = (item) => {
  const candidates = [
    item?.city,
    stripSuffix(item?.city),
    item?.province,
    stripSuffix(item?.province),
    normalizeProvinceName(item?.province),
    item?.name,
    stripSuffix(item?.name)
  ].filter(Boolean)

  for (const key of candidates) {
    const coord = chinaCityCoords[key]
    if (coord) return { coord, label: stripSuffix(item?.city || item?.province || item?.name || key) }
  }

  return null
}

const resolveWorldCoord = (item) => {
  if ((item?.country || '中国') === '中国') {
    return resolveChinaCoord(item)
  }

  const exactCandidates = [
    item?.city,
    stripSuffix(item?.city),
    item?.name,
    stripSuffix(item?.name)
  ].filter(Boolean)

  for (const key of exactCandidates) {
    const coord = worldCityCoords[key] || chinaCityCoords[key]
    if (coord) return { coord, label: stripSuffix(item?.city || item?.name || key) }
  }

  return null
}

const resolvePointCoord = (item, resolver) => {
  const lng = Number(item?.lng)
  const lat = Number(item?.lat)

  if (Number.isFinite(lng) && Number.isFinite(lat)) {
    return {
      coord: [lng, lat],
      label: stripSuffix(item?.city || item?.name || item?.province || item?.country || '')
    }
  }

  return resolver(item)
}

const normalizeTravelList = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.travels)) return payload.travels
  return []
}

const normalizeTravelStats = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { totalCities: 0, totalProvinces: 0, totalCount: 0, cityStats: {}, provinceStats: {} }
  }

  return {
    totalCities: payload.totalCities ?? payload.cityCount ?? payload.cities?.length ?? Object.keys(payload.cityStats || {}).length,
    totalProvinces: payload.totalProvinces ?? payload.provinceCount ?? payload.provinces?.length ?? Object.keys(payload.provinceStats || {}).length,
    totalCount: payload.totalCount ?? payload.travels?.length ?? payload.list?.length ?? 0,
    cityStats: payload.cityStats || {},
    provinceStats: payload.provinceStats || {}
  }
}

const normalizeTravelMap = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { totalCount: 0, totalCities: 0, totalCountries: 0, points: [], chinaPoints: [], worldPoints: [] }
  }

  const points = Array.isArray(payload.points) ? payload.points : []
  const chinaPoints = Array.isArray(payload.chinaPoints)
    ? payload.chinaPoints
    : points.filter((item) => (item.country || '中国') === '中国')
  const worldPoints = Array.isArray(payload.worldPoints)
    ? payload.worldPoints
    : points.filter((item) => (item.country || '中国') !== '中国')

  return {
    totalCount: payload.totalCount || payload.totalPoints || points.length,
    totalCities: payload.totalCities || 0,
    totalCountries: payload.totalCountries || 0,
    points,
    chinaPoints,
    worldPoints
  }
}

const countryOptions = computed(() => {
  const nameObj = countries.getNames('zh', { select: 'official' })
  return ['中国', ...Object.values(nameObj).filter(Boolean).filter(name => name !== '中国').sort((a, b) => a.localeCompare(b, 'zh'))]
})

const worldCityOptions = computed(() => Object.keys(worldCityCoords).sort((a, b) => a.localeCompare(b, 'zh')))
const regionCascaderProps = { value: 'label', label: 'label', children: 'children' }
const previewVisible = ref(false)
const previewList = ref([])
const previewIdx = ref(0)

const travels = ref([])
const travelListExpanded = ref(true)
const loadingTravels = ref(false)
const travelStats = ref(null)
const travelDialogVisible = ref(false)
const travelEditing = ref(null)
const travelForm = reactive({ city: '', province: '', country: '中国', longitude: '', latitude: '', dateRange: null, description: '' })
const submittingTravel = ref(false)
const regionCodes = ref([])

const chinaMapRef = ref(null)
const worldMapRef = ref(null)
const mapPoints = ref(null)
let chinaChart = null
let worldChart = null
let chinaGeoLoaded = false
let worldGeoLoaded = false

const onRegionChange = (vals) => {
  if (!vals || !vals.length) {
    travelForm.province = ''
    travelForm.city = ''
    return
  }
  travelForm.province = vals[0] || ''
  travelForm.city = vals[1] || vals[0] || ''
}

const onCountryChange = (country) => {
  if (country === '中国') {
    travelForm.province = ''
    travelForm.city = ''
    travelForm.longitude = ''
    travelForm.latitude = ''
    regionCodes.value = []
    return
  }
  regionCodes.value = []
  travelForm.province = ''
  travelForm.city = ''
  travelForm.longitude = ''
  travelForm.latitude = ''
}

const fetchTravels = async () => {
  loadingTravels.value = true
  try {
    const data = await getTravelList()
    travels.value = normalizeTravelList(data)
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loadingTravels.value = false
  }
}

const fetchTravelStats = async () => {
  try {
    const data = await getTravelStats()
    travelStats.value = normalizeTravelStats(data)
  } catch {
    travelStats.value = normalizeTravelStats(null)
  }
}

const resolveRegionCodes = (province, city) => {
  if (!province) return []
  if (!city || city === province) return [province]
  return [province, city]
}

const openTravelCreate = () => {
  travelEditing.value = null
  travelForm.city = ''
  travelForm.province = ''
  travelForm.country = '中国'
  travelForm.longitude = ''
  travelForm.latitude = ''
  travelForm.dateRange = null
  travelForm.description = ''
  regionCodes.value = []
  travelDialogVisible.value = true
}

const openTravelEdit = (item) => {
  travelEditing.value = item.id
  travelForm.city = item.city || ''
  travelForm.province = item.province || ''
  travelForm.country = item.country || '中国'
  travelForm.longitude = item.longitude || item.lng || ''
  travelForm.latitude = item.latitude || item.lat || ''
  travelForm.dateRange = item.startDate && item.endDate ? [item.startDate, item.endDate] : null
  travelForm.description = item.description || ''
  regionCodes.value = (item.country === '中国' || !item.country) ? resolveRegionCodes(item.province, item.city) : []
  travelDialogVisible.value = true
}

const submitTravel = async () => {
  if (!travelForm.city) return ElMessage.warning('请选择城市')
  if (travelForm.country !== '中国') {
    const longitude = Number(travelForm.longitude)
    const latitude = Number(travelForm.latitude)
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return ElMessage.warning('国外地点请填写有效的经纬度')
    }
  }
  submittingTravel.value = true
  const payload = {
    city: travelForm.city,
    province: travelForm.province || undefined,
    country: travelForm.country || '中国',
    longitude: travelForm.country !== '中国' ? String(travelForm.longitude).trim() || undefined : undefined,
    latitude: travelForm.country !== '中国' ? String(travelForm.latitude).trim() || undefined : undefined,
    startDate: travelForm.dateRange?.[0] || undefined,
    endDate: travelForm.dateRange?.[1] || undefined,
    description: travelForm.description?.trim() || undefined
  }
  try {
    if (travelEditing.value) {
      await updateTravel(travelEditing.value, payload)
    } else {
      await createTravel(payload)
    }
    travelDialogVisible.value = false
    await Promise.all([fetchTravels(), fetchTravelStats(), fetchMapData()])
  } catch (e) {
    ElMessage.error(e.message || '保存失败')
  } finally {
    submittingTravel.value = false
  }
}

const removeTravel = async (item) => {
  try {
    await ElMessageBox.confirm(`确定删除「${item.city}」的旅行记录？`, '删除确认', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
    await delTravel(item.id)
    await Promise.all([fetchTravels(), fetchTravelStats(), fetchMapData()])
  } catch {
    // user cancelled
  }
}

const fetchGeoJSON = async (primaryUrl, fallbackUrl) => {
  try {
    const resp = await fetch(primaryUrl)
    if (!resp.ok) throw new Error(resp.status)
    return await resp.json()
  } catch {
    if (fallbackUrl) {
      const resp2 = await fetch(fallbackUrl)
      if (!resp2.ok) throw new Error(resp2.status)
      return await resp2.json()
    }
    throw new Error('GeoJSON load failed')
  }
}

const loadChinaGeo = async () => {
  if (chinaGeoLoaded) return
  try {
    const geo = await fetchGeoJSON('/geo/china.json', 'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json')
    echarts.registerMap('china', geo)
    chinaGeoLoaded = true
  } catch {
    console.warn('China GeoJSON load failed')
  }
}

const loadWorldGeo = async () => {
  if (worldGeoLoaded) return
  try {
    const geo = await fetchGeoJSON('/geo/world.json', 'https://cdn.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json')
    echarts.registerMap('world', geo)
    worldGeoLoaded = true
  } catch {
    console.warn('World GeoJSON load failed')
  }
}

const renderMaps = async () => {
  await nextTick()
  const pts = mapPoints.value
  if (!pts) return

  if (chinaMapRef.value && chinaGeoLoaded) {
    if (!chinaChart) chinaChart = echarts.init(chinaMapRef.value)
    const chinaData = (pts.chinaPoints || []).map((p) => {
      const resolved = resolvePointCoord(p, resolveChinaCoord)
      return resolved ? { name: resolved.label, value: [...resolved.coord, 1], item: p } : null
    }).filter(Boolean)
    chinaChart.setOption({
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const d = p.data?.item
          if (!d) return p.name
          let s = `<strong>${d.city || d.name}</strong>`
          if (d.checkInTime || d.startDate) s += `<br/>${d.checkInTime || d.startDate}`
          if (d.endTime || d.endDate) s += ` ~ ${d.endTime || d.endDate}`
          if (d.description) s += `<br/>${d.description}`
          return s
        }
      },
      geo: {
        map: 'china', roam: true, zoom: 1.2,
        itemStyle: { areaColor: '#f5ece8', borderColor: '#d4c2c2' },
        emphasis: { itemStyle: { areaColor: '#ffdad9' } },
        label: { show: false }
      },
      series: [{
        type: 'effectScatter', coordinateSystem: 'geo',
        data: chinaData,
        symbolSize: 10,
        rippleEffect: { brushType: 'stroke', scale: 4 },
        itemStyle: { color: '#7b5455', shadowBlur: 10, shadowColor: 'rgba(123,84,85,0.4)' },
        label: { show: true, formatter: '{b}', position: 'right', fontSize: 11, color: '#504444' }
      }]
    }, true)
    chinaChart.resize()
  }

  if (worldMapRef.value && worldGeoLoaded) {
    if (!worldChart) worldChart = echarts.init(worldMapRef.value)
    const worldData = (pts.worldPoints || pts.points || []).map((p) => {
      const resolved = resolvePointCoord(p, resolveWorldCoord)
      return resolved ? { name: resolved.label, value: [...resolved.coord, 1], item: p } : null
    }).filter(Boolean)
    worldChart.setOption({
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const d = p.data?.item
          if (!d) return p.name
          let s = `<strong>${d.city || d.name}</strong>`
          if (d.country) s += `  ${d.country}`
          if (d.checkInTime || d.startDate) s += `<br/>${d.checkInTime || d.startDate}`
          if (d.endTime || d.endDate) s += ` ~ ${d.endTime || d.endDate}`
          if (d.description) s += `<br/>${d.description}`
          return s
        }
      },
      geo: {
        map: 'world', roam: true, zoom: 1.2,
        itemStyle: { areaColor: '#f5ece8', borderColor: '#d4c2c2' },
        emphasis: { itemStyle: { areaColor: '#ffdad9' } },
        label: { show: false }
      },
      series: [{
        type: 'effectScatter', coordinateSystem: 'geo',
        data: worldData,
        symbolSize: 10,
        rippleEffect: { brushType: 'stroke', scale: 4 },
        itemStyle: { color: '#7b5455', shadowBlur: 10, shadowColor: 'rgba(123,84,85,0.4)' },
        label: { show: true, formatter: '{b}', position: 'right', fontSize: 11, color: '#504444' }
      }]
    }, true)
    worldChart.resize()
  }
}

const fetchMapData = async () => {
  try {
    const data = await getTravelMap()
    mapPoints.value = normalizeTravelMap(data)
    renderMaps()
  } catch {
    // ignore map fetch failures
  }
}

const onResize = () => {
  chinaChart?.resize()
  worldChart?.resize()
}

onMounted(async () => {
  await Promise.all([fetchTravels(), fetchTravelStats(), loadChinaGeo(), loadWorldGeo()])
  await fetchMapData()
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  chinaChart?.dispose()
  worldChart?.dispose()
  chinaChart = null
  worldChart = null
})
</script>

<style scoped>
/* ═══════ Design Tokens ═══════ */
.mv2-page {
  --mv-bg: #fff8f5;
  --mv-surface: #f5ece8;
  --mv-surface-high: #efe6e2;
  --mv-surface-low: #fbf2ed;
  --mv-primary: #7b5455;
  --mv-primary-container: #f4c2c2;
  --mv-secondary: #735c00;
  --mv-secondary-container: #fed65b;
  --mv-on-surface: #1f1b18;
  --mv-on-surface-variant: #504444;
  --mv-outline: #827473;
  --mv-outline-variant: #d4c2c2;
  --mv-radius: 12px;
  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 40px 64px;
  display: flex; flex-direction: column; gap: 48px;
}

/* ═══════ Hero ═══════ */
.mv2-hero { text-align: center; padding: 32px 0 16px; }
.mv2-hero h1 {
  font-family: 'Noto Serif', serif;
  font-size: 48px; font-weight: 400; line-height: 1.2;
  color: var(--mv-primary); margin: 0 0 8px;
}
.mv2-hero p {
  font-size: 18px; line-height: 1.6;
  color: var(--mv-on-surface-variant);
  max-width: 600px; margin: 0 auto;
}

/* ═══════ Maps Grid ═══════ */
.mv2-maps {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  align-items: stretch;
}
.mv2-map-card {
  background: #fff;
  border-radius: var(--mv-radius);
  padding: 32px;
  border: 1px solid rgba(212,194,194,0.3);
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
  display: flex; flex-direction: column; gap: 16px;
  overflow: hidden;
  min-height: 560px;
}
.mv2-map-card__head {
  display: flex; justify-content: space-between; align-items: center;
}
.mv2-map-card__head h2 {
  font-family: 'Noto Serif', serif;
  font-size: 24px; font-weight: 500;
  color: var(--mv-on-surface); margin: 0;
}
.mv2-map-card__badge {
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  color: var(--mv-secondary);
  background: rgba(254,214,91,0.2);
  padding: 4px 12px; border-radius: 999px;
}
.mv2-map-card__canvas {
  width: 100%; height: 460px;
  background: var(--mv-surface);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(212,194,194,0.2);
}

/* ═══════ Shared buttons ═══════ */
.mv2-btn-outline {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--mv-surface-high);
  background: var(--mv-surface-low);
  color: var(--mv-on-surface-variant);
  font-size: 13px; font-weight: 600;
  padding: 6px 16px; border-radius: 999px;
  cursor: pointer; transition: all 0.2s;
}
.mv2-btn-outline:hover {
  background: var(--mv-primary-container);
  border-color: var(--mv-primary-container);
  color: var(--mv-primary);
}
.mv2-btn-primary {
  display: inline-flex; align-items: center; gap: 4px;
  border: none;
  background: var(--mv-primary-container);
  color: var(--mv-primary);
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  padding: 8px 18px; border-radius: 999px;
  cursor: pointer; transition: all 0.2s;
}
.mv2-btn-primary:hover {
  background: var(--mv-primary);
  color: #fff;
}
.mv2-btn-icon {
  border: 1px solid var(--mv-surface-high);
  background: transparent;
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--mv-on-surface-variant);
  transition: all 0.2s;
}
.mv2-btn-icon:hover { background: var(--mv-surface); }

/* ═══════ Travels Section ═══════ */
.mv2-travels-section {
  background: #fff;
  border-radius: var(--mv-radius);
  padding: 24px;
  border: 1px solid rgba(212,194,194,0.3);
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
}
.mv2-travels-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 4px;
}
.mv2-travels-head__left {
  display: flex; align-items: center; gap: 8px;
  font-size: 16px; font-weight: 600; color: var(--mv-on-surface);
}
.mv2-travels-head__left .material-symbols-outlined {
  color: var(--mv-primary); font-size: 22px;
}
.mv2-travels-count {
  font-size: 12px; font-weight: 600;
  background: var(--mv-surface);
  color: var(--mv-on-surface-variant);
  padding: 2px 10px; border-radius: 999px;
}
.mv2-travels-head__right {
  display: flex; align-items: center; gap: 8px;
}

.mv-collapse-enter-active { animation: mv-slide-down 0.3s ease; }
.mv-collapse-leave-active { animation: mv-slide-down 0.3s ease reverse; }
@keyframes mv-slide-down {
  from { opacity: 0; max-height: 0; overflow: hidden; }
  to { opacity: 1; max-height: 2000px; overflow: hidden; }
}

.mv2-travel-list {
  position: relative; padding-left: 24px; margin-top: 16px;
  display: flex; flex-direction: column; gap: 0;
}
.mv2-travel-list::before {
  content: ''; position: absolute; left: 7px; top: 8px; bottom: 8px;
  width: 2px; background: var(--mv-primary-container);
  border-radius: 2px;
}
.mv2-travel-item { position: relative; padding-bottom: 20px; }
.mv2-travel-item:last-child { padding-bottom: 0; }
.mv2-travel-item__dot {
  position: absolute; left: -20px; top: 6px;
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--mv-primary);
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(123,84,85,0.3);
}
.mv2-travel-item__card {
  background: var(--mv-surface-low);
  border: 1px solid var(--mv-surface-high);
  border-radius: 8px; padding: 16px 18px;
  transition: all 0.2s;
}
.mv2-travel-item__card:hover { border-color: var(--mv-primary-container); box-shadow: 0 4px 16px rgba(123,84,85,0.06); }
.mv2-travel-item__top { display: flex; align-items: baseline; gap: 8px; }
.mv2-travel-item__top strong { font-size: 16px; color: var(--mv-on-surface); }
.mv2-travel-item__sub { font-size: 12px; color: var(--mv-on-surface-variant); }
.mv2-travel-item__desc { margin: 6px 0 0; font-size: 14px; color: var(--mv-on-surface-variant); line-height: 1.6; }
.mv2-travel-item__date { margin-top: 8px; font-size: 12px; color: var(--mv-outline); }
.mv2-travel-item__ops { margin-top: 10px; display: flex; gap: 8px; }
.mv2-travel-item__ops button {
  border: 1px solid var(--mv-surface-high);
  background: transparent;
  border-radius: 999px; padding: 4px 14px;
  font-size: 12px; font-weight: 600; color: var(--mv-on-surface-variant);
  cursor: pointer; transition: all 0.2s;
}
.mv2-travel-item__ops button:hover { background: var(--mv-surface-high); }
.mv2-travel-item__ops button.danger { color: #ba1a1a; }
.mv2-travel-item__ops button.danger:hover { background: rgba(186,26,26,0.08); border-color: rgba(186,26,26,0.2); }

/* ═══════ Bucket List ═══════ */
.mv2-bucket {
  display: flex; flex-direction: column; gap: 24px;
}
.mv2-bucket__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  border-bottom: 1px solid rgba(212,194,194,0.4);
  padding-bottom: 16px;
  flex-wrap: wrap; gap: 12px;
}
.mv2-bucket__head h2 {
  font-family: 'Noto Serif', serif;
  font-size: 32px; font-weight: 400; line-height: 1.3;
  color: var(--mv-primary); margin: 0;
}
.mv2-bucket__head > div > p {
  font-size: 16px; line-height: 1.6;
  color: var(--mv-on-surface-variant); margin: 4px 0 0;
}
.mv2-bucket__actions {
  display: flex; align-items: center; gap: 12px;
}
.mv2-filter-tabs {
  display: flex; gap: 4px;
  background: var(--mv-surface);
  border-radius: 999px; padding: 3px;
}
.mv2-filter-tabs button {
  border: 0; background: transparent; border-radius: 999px;
  padding: 6px 18px; font-size: 13px; font-weight: 600;
  color: var(--mv-on-surface-variant);
  cursor: pointer; transition: all 0.2s;
}
.mv2-filter-tabs button.active {
  background: #fff; color: var(--mv-primary);
  box-shadow: 0 2px 8px rgba(123,84,85,0.1);
}

/* ═══════ Badges ═══════ */
.mv2-badges { display: flex; gap: 10px; flex-wrap: wrap; }
.mv2-badge {
  display: flex; align-items: center; gap: 6px;
  background: rgba(254,214,91,0.15);
  border: 1px solid rgba(254,214,91,0.3);
  border-radius: 999px; padding: 5px 14px;
  font-size: 12px; font-weight: 600; color: var(--mv-secondary);
}
.mv2-badge__icon { font-size: 16px; }

/* ═══════ Bucket Grid ═══════ */
.mv2-bucket-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.mv2-bucket-card {
  background: #fff;
  border-radius: var(--mv-radius);
  border: 1px solid rgba(212,194,194,0.3);
  padding: 24px;
  display: flex; gap: 16px; align-items: flex-start;
  transition: all 0.2s;
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
  position: relative;
}
.mv2-bucket-card:hover {
  border-color: rgba(123,84,85,0.4);
}
.mv2-bucket-check {
  border: none; background: transparent;
  cursor: pointer; flex-shrink: 0; padding: 0; margin-top: 2px;
  transition: transform 0.2s;
}
.mv2-bucket-check:hover { transform: scale(1.1); }
.mv2-bucket-card__body {
  flex: 1; cursor: pointer;
  display: flex; flex-direction: column; gap: 8px;
}
.mv2-bucket-card__body h3 {
  font-family: 'Noto Serif', serif;
  font-size: 20px; font-weight: 500;
  color: var(--mv-on-surface); margin: 0;
  line-height: 1.4;
}
.mv2-bucket-card__body h3.done {
  text-decoration: line-through;
  opacity: 0.7;
}
.mv2-bucket-card__desc {
  font-size: 14px; color: var(--mv-on-surface-variant);
  line-height: 1.5; margin: 0;
}
.mv2-bucket-card__meta {
  display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px;
}
.mv2-tag {
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  color: var(--mv-secondary);
  background: rgba(254,214,91,0.2);
  padding: 2px 8px; border-radius: 6px;
}
.mv2-tag--muted {
  background: var(--mv-surface);
  color: var(--mv-on-surface-variant);
}
.mv2-bucket-del {
  position: absolute; top: 8px; right: 8px;
  border: 0; background: transparent;
  color: var(--mv-outline); cursor: pointer;
  padding: 4px; border-radius: 6px;
  opacity: 0; transition: all 0.2s;
}
.mv2-bucket-card:hover .mv2-bucket-del { opacity: 1; }
.mv2-bucket-del:hover { color: #ba1a1a; background: rgba(186,26,26,0.06); }
.mv2-bucket-card--done {
  background: rgba(123,84,85,0.02);
}

/* ═══════ Shared ═══════ */
.mv2-empty {
  text-align: center; padding: 40px 0;
  color: var(--mv-on-surface-variant);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.mv2-empty p { margin: 0; }
.mv2-load-more { text-align: center; margin-top: 12px; }
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24;
  vertical-align: middle;
}

.mv-fs-enter-active, .mv-fs-leave-active { transition: opacity 0.3s ease; }
.mv-fs-enter-from, .mv-fs-leave-to { opacity: 0; }

/* ═══════ Responsive ═══════ */
@media (max-width: 900px) {
  .mv2-maps { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .mv2-page { padding: 0 16px 40px; gap: 32px; }
  .mv2-hero h1 { font-size: 32px; }
  .mv2-hero p { font-size: 15px; }
  .mv2-map-card { padding: 20px; }
  .mv2-map-card { min-height: 0; }
  .mv2-map-card__canvas { height: 360px; }
  .mv2-map-card__head h2 { font-size: 20px; }
  .mv2-travels-head { flex-direction: column; align-items: flex-start; gap: 10px; }
}
@media (max-width: 480px) {
  .mv2-hero h1 { font-size: 28px; }
  .mv2-map-card__canvas { height: 300px; }
}
</style>

<style>
/* ====== Image preview overlay (unscoped for Teleport) ====== */
.mv-preview-mask {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.88);
  display: flex; align-items: center; justify-content: center;
  animation: mv-fade-in 0.25s ease;
}
@keyframes mv-fade-in { from { opacity: 0; } to { opacity: 1; } }
.mv-preview-img {
  max-width: 90vw; max-height: 85vh;
  object-fit: contain; border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
  user-select: none;
}
.mv-preview-close {
  position: absolute; top: 16px; right: 20px;
  background: rgba(255,255,255,0.15); border: 0;
  width: 40px; height: 40px; border-radius: 50%;
  font-size: 24px; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(8px);
  transition: background 0.2s;
}
.mv-preview-close:hover { background: rgba(255,255,255,0.3); }
.mv-preview-arrow {
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.12); border: 0;
  width: 48px; height: 48px; border-radius: 50%;
  font-size: 28px; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(8px);
  transition: background 0.2s;
}
.mv-preview-arrow:hover { background: rgba(255,255,255,0.25); }
.mv-preview-arrow--left { left: 20px; }
.mv-preview-arrow--right { right: 20px; }
.mv-preview-counter {
  position: absolute; bottom: 24px; left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.5); backdrop-filter: blur(8px);
  padding: 6px 16px; border-radius: 999px;
  font-size: 13px; color: rgba(255,255,255,0.85);
  letter-spacing: 1px;
}
</style>
